import styled from '@emotion/styled'
import { Trans } from '@lingui/macro'
import { useState } from 'react'

import { PrismicRichText, RichTextBlock } from '@emico/prismic'
import { maxWidth } from '@emico/styles'
import { useBreakpoints } from '@emico/ui'

import {
    Category as PrismicCategory,
    CategoryBodySeoFooter,
} from './graphql/schema.generated'
import getHeadingDecorationVisibility from './HeadingDecoration'
import Button from './input/Button'
import Container from './layout/Container'
import styles from './PrismicCategorySeoFooter.module.scss'
import Heading from './typography/Heading'
import Text from './typography/Text'

const StyledSection = styled.section`
    margin: 60px 0;
    @media ${maxWidth('sm')} {
        margin: 20px 0;
    }
`

const StyledText = styled(Text)`
    margin-bottom: 1em;
`

const StyledHeading = styled(Heading)`
    margin-bottom: 0.5em;
`

const StyledMoreLessButton = styled(Button)`
    color: ${process.env.REACT_APP_PRIMARY_COLOR};
    &:focus {
        outline: 0;
    }
`

interface FooterProps {
    content: PrismicCategory | undefined
}

const PrismicCategorySeoFooter = ({ content }: FooterProps) => {
    const { isMobile } = useBreakpoints()

    const CategoryBodySeoFooterContent = content?.body?.find(
        (slice) => slice.__typename === 'CategoryBodySeoFooter',
    ) as CategoryBodySeoFooter | undefined

    const seoHeader = CategoryBodySeoFooterContent?.primary?.seoHeader
    const seoDescription = CategoryBodySeoFooterContent?.primary?.seoDescription
    const seoExtendedDescription =
        CategoryBodySeoFooterContent?.primary?.seoExtendedDescription

    const fields = CategoryBodySeoFooterContent?.fields ?? []
    const hasFields = fields.length > 0

    const [isCollapsed, setCollapsed] = useState<boolean>(false)

    if (!seoHeader && (!seoDescription || !seoExtendedDescription)) {
        return null
    }

    return (
        <StyledSection
            css={getHeadingDecorationVisibility(false)}
            className={styles.section}
        >
            <Container>
                {seoHeader && (
                    <StyledHeading variant="h2" element="h2">
                        {seoHeader}
                    </StyledHeading>
                )}

                {seoExtendedDescription && (
                    <PrismicRichText
                        render={seoExtendedDescription as [RichTextBlock]}
                        analyticsContext="prismic.categorySeo.content"
                    />
                )}
                {seoDescription && <StyledText>{seoDescription}</StyledText>}

                {hasFields &&
                    fields.map((item, index) => (
                        <div
                            key={index}
                            style={
                                isMobile
                                    ? !isCollapsed
                                        ? { display: 'none' }
                                        : { display: 'block' }
                                    : undefined
                            }
                        >
                            {item.header && (
                                <StyledHeading
                                    element="h3"
                                    variant="h3"
                                    className={styles.headingThree}
                                >
                                    {item.header}
                                </StyledHeading>
                            )}

                            {item.content && (
                                <PrismicRichText
                                    render={item.content as [RichTextBlock]}
                                    analyticsContext="prismic.categorySeo.content"
                                />
                            )}
                        </div>
                    ))}
                {isMobile && hasFields && (
                    <StyledMoreLessButton
                        name="Stores"
                        variant="link"
                        category="header"
                        minWidth
                        onClick={() => {
                            setCollapsed(!isCollapsed)
                        }}
                    >
                        {!isCollapsed ? (
                            <Trans id="catalog.categoryPage.seo.readmoretext">
                                Read more
                            </Trans>
                        ) : (
                            <Trans id="catalog.categoryPage.seo.readlesstext">
                                Read less
                            </Trans>
                        )}
                    </StyledMoreLessButton>
                )}
            </Container>
        </StyledSection>
    )
}

export default PrismicCategorySeoFooter
